import React, { useState, useCallback } from "react";
import toast from "react-hot-toast";
import styles from "./RemindMeButton.module.css";

/**
 * Props:
 *  - message, company, role, targetPerson — reminder context
 *  - onCreateReminder — from useReminders().createReminder
 *  - reminders — from useReminders().reminders (to detect existing)
 */
const RemindMeButton = ({
  message,
  company,
  role,
  targetPerson,
  onCreateReminder,
  reminders = [],
  onReminderCreated,
}) => {
  const [isCreating, setIsCreating] = useState(false);

  // Derive "already set" from the shared reminders list
  const alreadySet = reminders.some(
    (r) =>
      r.company?.toLowerCase() === company?.toLowerCase() &&
      r.role?.toLowerCase() === role?.toLowerCase()
  );

  const handleClick = useCallback(async () => {
    if (isCreating || alreadySet) return;
    setIsCreating(true);
    try {
      const result = await onCreateReminder({ message, company, role, targetPerson });
      if (result.success) {
        toast.success(result.data.message, { duration: 4000 });
        if (onReminderCreated) onReminderCreated();
      } else if (result.duplicate) {
        const date = result.data.reminder?.reminderDate
          ? new Intl.DateTimeFormat("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
            }).format(new Date(result.data.reminder.reminderDate))
          : "a future date";
        toast(`You already have a reminder set for ${company} on ${date}`, {
          icon: "ℹ️",
          duration: 4000,
        });
      }
    } catch {
      toast.error("Failed to set reminder. Please try again.");
    } finally {
      setIsCreating(false);
    }
  }, [isCreating, alreadySet, message, company, role, targetPerson, onCreateReminder, onReminderCreated]);

  return (
    <button
      className={`${styles.btn} ${alreadySet ? styles.btnSet : ""}`}
      onClick={handleClick}
      disabled={isCreating || alreadySet}
      aria-label={
        alreadySet
          ? "Reminder already set"
          : "Set a follow-up reminder in 3 days"
      }
      aria-busy={isCreating}
    >
      {isCreating ? (
        <>
          <span className={styles.spinner} aria-hidden="true" />
          Setting…
        </>
      ) : alreadySet ? (
        "✅ Reminder set!"
      ) : (
        "🔔 Remind"
      )}
    </button>
  );
};

export default RemindMeButton;
